import * as React from "react";
import useSWR from "swr";
import { withCharacterOrder } from "../domain/shopImages";
import { sendShopImageRequest, shopImagesUrl } from "../services/shopImages";
import fetcher from "./fetcher";

const EMPTY_IMAGES = Object.freeze({
  headshot: null,
  banner: null,
  character: [],
});

const OPTIMISTIC = Object.freeze({
  reorderCharacters: ({ imageIds }) => withCharacterOrder(imageIds),
});

const optimisticDataFor = (action, args) => {
  const reshape = OPTIMISTIC[action]?.(args);

  return reshape
    ? (current) => current && { ...current, images: reshape(current.images) }
    : undefined;
};

export default function useShopImages(influencerId) {
  const { data, error, mutate } = useSWR(
    influencerId ? shopImagesUrl(influencerId) : null,
    fetcher,
  );

  const perform = React.useCallback(
    async (action, args) => {
      const outcome = {};

      await mutate(
        async (current) => {
          outcome.result = await sendShopImageRequest({
            influencerId,
            action,
            args,
          });
          return { ...current, images: outcome.result.images };
        },
        {
          optimisticData: optimisticDataFor(action, args),
          rollbackOnError: true,
          revalidate: false,
        },
      );

      return outcome.result;
    },
    [influencerId, mutate],
  );

  return {
    images: data?.images ?? EMPTY_IMAGES,
    isLoading: Boolean(influencerId) && !data && !error,
    error,
    perform,
    refresh: mutate,
  };
}
